import { LookUp } from "../data_type/Lookup.model";
import { DataType } from "../model/DataType.model";
import { DefaultLayoutId } from "./DefaultLayoutId.model";

/**
 *  컴포넌트 분류
 *  ex) Button, Menu, Layout
 * @since 0.0.1
 * @author Heungsoo Ham
 * @enum
 */
export enum ComponentCategory {
    CUSTOM = "Custom Components",
    INPUT = "Fields",
    BUTTON = "Buttons",
    LABEL = "Labels",
    DATA = "Data",
    PANEL = "Layouts and Views",
    MENU = "Menus",
    CHART = "Chart",
    TREE = "Tree",
    CALENDAR = "Calendar",
    FILE = "File",
    MISC = "Misc",
    HANDLER = "Handler"
}

/**
 *  컴포넌트 레이아웃 분류
 *  ex) Panel, Grid
 * @since 0.0.1
 * @author Heungsoo Ham
 * @enum
 */
export enum LayoutCategory {
    PANEL = "Panel"
}

/**
 *  앱툴 분류
 *  ex) Panel => 최초 패널로 뜸
 *  ex) modal => 최초 모달로 뜸
 * @since 0.0.1
 * @author Heungsoo Ham
 * @enum
 */
export enum AppToolCategory {
    TOOL = "Tool",
    MANAGER = "Manager"
}

/**
 *  컴포넌트 중, 모달, 팝업, 익셉션, 블럭 핸들러
 * @since 0.0.1
 * @author Heungsoo Ham
 * @enum
 */
export enum HandlerType {
    BLOCK = "_blockHandler",
    MODAL = "_modalHandler",
    EXCEPTION = "_exceptionHandler",
    POPUP = "_popupHandler"
}

/**
 *  컴포넌트 엘리먼트의 매니패스트, 코어 엔진은 이 값을 읽어 컴퍼넌트 앨리먼트의 특성을 파악한다.
 * @since 0.0.1
 * @author Heungsoo Ham
 * @interface
 */
export interface ComponentElementManifest {
    readonly uniqueId: string | HandlerType;
    readonly category: ComponentCategory | LayoutCategory | AppToolCategory;
    readonly defaultLayoutId?: DefaultLayoutId;
    readonly label: string;
    readonly description: string;
    readonly icon: string;
    readonly dataType?: Array<DataType>;
    readonly onlyArray?: boolean;
    readonly pathToLimitDataTypeArray?: string;
    readonly property: ComponentElementProperty;
}

/**
 * 컴포넌트 엘리먼트의 매니패스트 중 컴퍼넌트 앨리먼트의 커스텀 프로퍼티 형식
 * @since 0.0.1
 * @author Heungsoo Ham
 * @enum
 */
export enum ComponentElementPropertyType {
    _INTERNAL = "_internal",
    ACCESSIBILITY = "accessibility",
    INPUT = "input",
    ALIASID = "aliasId",
    LABELFOR = "labelFor",
    TABINDEX = "tabIndex",
    TEXT = "text",
    DATE = "date",
    MONTH = "month",
    TIME = "time",
    COLOR = "color",
    BOOLEAN = "boolean",
    TRISTATE = "triState",
    JSON = "json",
    STYLE = "style",
    PROPERTY = "property",
    PROPERTYARRAY = "propertyArray",
    PROPERTYRECURSIVE = "propertyRecursive",
    ATTRIBUTE = "attribute",
    MENUMODEL = "menuModel",
    FUNCTION = "function",
    EDITABLEJSON = "editableJson",
    REPEATABLE = "repeatable",
    FORMSTATE = "formState",
    CLASSLIST = "classList",
    MULTIPLESELECT = "multipleSelect",
    FORMATDATE = "formatDate",
    FORMATTIME = "formatTime",
    FORMATDATETIME = "formatDateTime",
    FORMATYEARMONTH = "formatYearMonth",
    LINKFORM = "linkForm",
    HTML = "html",
    CUSTOMHTML = "customHtml",
    STEP = "step",
    CURRENCY = "currency",
    SPINNER = "spinner",
    INPUTINTEGER = "inputInteger",
    INPUTNUMBER = "inputNumber",
    GRIDACTION = "gridAction",
    CHIPS = "chips",
    LOOKUPFILTER = "lookupFilter",
    LOOKUPAPIFILTER = "lookupAPIFilter",
    LOOKUPLABEL = "lookupLabel",
    GRIDVALIDATIONFORCOMPLETE = "gridValidationForComplete",
    CUSTOMVALIDATIONFORCOMPLETE = "customValidationForComplete",
    FILEUPLOADPARAMETER = "fileUploadParameter"
}

/**
 * @interface
 */
export interface ComponentElementPropertyAttribute {
    [key: string]: ComponentElementPropertyAttributeModel;
}
/**
 * @interface
 */
export interface ComponentElementPropertyAttributeModel {
    /** label $i18n 으로 시작하면 i18n 서비스 예약 */
    label?: string;
    /** value */
    value?: any | Array<any>;
    /** 상세 설명 $i18n 으로 시작하면 i18n 서비스 예약 */
    description?: string;
    /** type : value 가 배열이 아닌경우, 인풋, 텍스트, 날짜, 시간, 컬러, boolean 중 하나로 입력 받으려 할 경우 디폴트는 INPUT 이다,  lookUpData 가 있을 경우 무시 */
    type?: ComponentElementPropertyType;
    /** look up 형식, value 가 array 일 경우 체크박스, 복수인 경우 셀렉트 상자로 자동 출력 이 때 type 값은 무시된다 */
    lookUpData?: Array<LookUp>;
    lookUpMultiple?: boolean;
    /** 값 선택에 영향을 미치는 attribute name */
    dependencies?: Array<string>;
    /** 여러 Datatype을 지원하는 Component에 대해서 이 Attribute를 사용할 수 있는 Type */
    allowDataType?: Array<DataType>;
    /** 특정 type별 체크를 해야 할 것이 있을 때 사용 */
    limit?: {[key: string]: any};
    /** i18n */
    i18n?: boolean;
    [key: string]: any;
}

/**
 * @interface
 */
export interface ComponentElementPropertyEvent {
    /** onClick 인경우 key: click, Label 은 onClick 가 된다 */
    [key: string]: ComponentElementPropertyEventModel;
}
/**
 * @interface
 */
export interface ComponentElementPropertyEventModel {
    label: string;
    confirmMessage: string;
    action: string;
    formState: string;
    description?: string;
}
/**
 * @interface
 */
export interface ComponentElementPropertyInterLink {
    [key: string]: ComponentElementPropertyInterLinkModel;
}
/**
 * @interface
 */
export interface ComponentElementPropertyInterLinkModel {
    use?: boolean;
    description?: string;
    param?: {
        [key: string]: string;
    };
    return?: string;
}

/**
 * @interface
 */
const comInfo = {
    destroyed: "the component is destroyed, then true",
    accessibility: "accessibility is true, then true",
    elementId: "the elementId of component",
    applicationId: "application Id",
    formId: "form Id",
    dataType: "the data type of component like as string(single), [string](array) and so on",
    displayMode: "Editable(1), Readonly(2), Disabled(3), Printable(4)",
    defaultDateFormat: "default Date format",
    parentDom: "the parent dom of component"
};

export interface ComponentElementProperty {
    attribute?: ComponentElementPropertyAttribute;
    event?: ComponentElementPropertyEvent;
    innerAlias?: string;
    innerLink?: ComponentElementPropertyInterLink;
    outerAlias?: string;
    outerLink?: ComponentElementPropertyInterLink;
    extend?: any;
}

export const BuiltInInnerLink = {
    getComponentInfo: {
        description: "get information of this component",
        return: comInfo
    },
    focus: {
        description: "focus dom if possible"
    },
    subscription: {
        description: "subscription data value",
        param: {
            value: "data value"
        }
    },
    lookupDataSubscription: {
        description: "subscription lookup data",
        param: {
            lookup: "Array<{label: string, value: string, _object: any}> ",
            isLazyLoading: "boolean, if true, then lazy loading",
        }
    },
    requestLookupData: {
        description: "request new lookup data"
    },
    closeOverlay: {
        description: "close overlay if possible"
    },
    setProperty: {
        description: "set up property",
        param: {
            property: "attribute, event, innerAlias, outerAlias, innerLink, outerLink"
        }
    },
    getProperty: {
        description: "get property",
        return: "attribute, event, innerAlias, outerAlias, innerLink, outerLink"
    }
};

export const BuiltInOuterLink = {
    emit: {
        description: "emit data value",
        param: {
            value: "data value"
        }
    },
    emitSubscriptionReject: {
        description: "emit if the value or type of data is invalid"
    },
    emitDidCatch: {
        description: "emit error if possible",
        param: {
            error: "error"
        }
    },
    emitContainerKey: {
        description: "emit container key if possible",
        param: {
            value: "container key which is turned on"
        }
    },
    readyComponent: {
        description: "emit only once when to be ready component",
        param: {
            info: comInfo
        }
    }
};
